import { Base, Collection } from 'active-resource';
import { EmployerInterviewResource } from 'util/resources/EmployerInterview';
import { EmployerInterviewSlotResource } from 'util/resources/EmployerInterviewSlot';
import { EmployerProfileLocationResource } from 'util/resources/EmployerProfileLocation';
import { LocationResource } from 'util/resources/Location';
import APILibrary, { ClassExtender, GConstructor, GenerateResource } from './APILibrary';
import { AccountResource } from './Account';
import { EmployerResource } from './Employer';
import { SystemNotificationResource } from './SystemNotification';
import { EmployerSavedFilterSelectionResource } from 'util/resources/EmployerSavedFilterSelection';

/**
 * Relationship classes
 */
export class EmployerProfileAttributes {
  accountId: string = null;
  address: string = null;
  city: string = null;
  emailNotifications: boolean = null;
  firstName: string = null;
  id: string = null;
  lastName: string = null;
  phoneNumber: string = null;
  position: string = null;
  slug: string = null;
  smsNotifications: boolean = null;
  state: string = null;
  timeZone: string = null;
  calendarDaysInFuture: number = null;
  zipCode: string = null;
}

export class EmployerProfileBelongsTo {
  account: () => AccountResource = () => new AccountResource();
  employer: () => EmployerResource = () => new EmployerResource();
}

export class EmployerProfileHasMany {
  locations: () => Collection<LocationResource> = () => new Collection<LocationResource>();
  employerProfileLocations: () => Collection<EmployerProfileLocationResource> = () =>
    new Collection<EmployerProfileLocationResource>();
  interviewSlots: () => Collection<EmployerInterviewSlotResource> = () =>
    new Collection<EmployerInterviewSlotResource>();
  interviews: () => Collection<EmployerInterviewResource> = () =>
    new Collection<EmployerInterviewResource>();
  systemNotifications: () => Collection<SystemNotificationResource> = () =>
    new Collection<SystemNotificationResource>();
  savedFilterSelections: () => Collection<EmployerSavedFilterSelectionResource> = () =>
    new Collection<EmployerSavedFilterSelectionResource>();
  // TODO: If I add this I get infinite loops while loading
  // interviewEventTypes: () => Collection<EmployerInterviewEventTypeResource> = () =>
  //   new Collection<EmployerInterviewEventTypeResource>();
}

/**
 * Utility interfaces
 */
export interface EmployerProfileMethods {
  fullName: () => string;
  hasActivePlan: () => boolean;
}

export interface EmployerProfileInterface
  extends EmployerProfileAttributes,
    EmployerProfileBelongsTo,
    EmployerProfileHasMany,
    EmployerProfileType {}

type EmployerProfileType = GConstructor<EmployerProfileInterface>;

export interface EmployerProfileResource
  extends Base<EmployerProfileAttributes>,
    EmployerProfileInterface,
    EmployerProfileMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<EmployerProfileAttributes>({
    queryName: 'employer_profiles',
    className: 'EmployerProfile',
    AttributesClass: EmployerProfileAttributes,
    BelongsToClasses: [
      {
        Klass: EmployerProfileBelongsTo,
      },
    ],
    HasManyClasses: [
      {
        Klass: EmployerProfileHasMany,
      },
    ],
  });

export const EmployerProfileResourceClass = <TBase extends EmployerProfileType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements EmployerProfileMethods {
    fullName() {
      return `${this.firstName} ${this.lastName}`;
    }

    hasActivePlan() {
      return this.employer().hasActivePlan();
    }
  };

export class EmployerProfileResource extends ClassExtender<EmployerProfileInterface>(
  BaseClass,
  EmployerProfileResourceClass,
) {}

/**
 * Resource instance
 */
export const EmployerProfile =
  APILibrary.createResource<EmployerProfileResource>(EmployerProfileResource);
